import React from 'react'
import ReactDatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { deviceUses24HourClock } from '@lib/datetime'
import { Wrapper, DatePickerGlobalStyles } from './DateTimePicker.styles'

export type DateTimePickerProps = {
    id?: string
    value: string
    onChange: (value: string) => void
    minDate?: Date
    maxDate?: Date
    placeholder?: string
    showTime?: boolean
    disabled?: boolean
    /**
     * Force the time onto a 24-hour clock ("14:30") in both the input text and
     * the time dropdown. Flights always set this (aviation convention, issue
     * #164). Leave it unset and the picker follows the viewer's locale clock,
     * matching how non-flight times are displayed (issue #165) — the input and
     * the display must agree, or we recreate the very mismatch #164 reports.
     */
    use24HourTime?: boolean
}

const pad = (n: number) => String(n).padStart(2, '0')

const wallClockToDate = (s: string): Date | null => {
    if (!s) return null
    const [datePart, timePart = '00:00'] = s.split('T')
    const [y, m, d] = datePart.split('-').map(Number)
    const [hh, mm] = timePart.split(':').map(Number)
    if (!y || !m || !d) return null
    return new Date(y, m - 1, d, hh || 0, mm || 0, 0, 0)
}

const dateToWallClock = (d: Date, withTime: boolean): string => {
    const base = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    if (!withTime) return base
    return `${base}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export const DateTimePicker: React.FC<DateTimePickerProps> = ({
    id,
    value,
    onChange,
    minDate,
    maxDate,
    placeholder,
    showTime = true,
    disabled,
    use24HourTime,
}) => {
    const selected = wallClockToDate(value)
    // Unset means "follow the viewer" — flights pass `true` to override it.
    const is24Hour = use24HourTime ?? deviceUses24HourClock()
    const dateFormat = showTime
        ? is24Hour
            ? 'MMMM do, yyyy HH:mm'
            : 'MMMM do, yyyy h:mm aa'
        : 'MMMM do, yyyy'

    return (
        <>
            <DatePickerGlobalStyles />
            <Wrapper>
                <ReactDatePicker
                    id={id}
                    selected={selected}
                    onChange={(d: Date | null) => {
                        if (!d) {
                            onChange('')
                            return
                        }
                        onChange(dateToWallClock(d, showTime))
                    }}
                    showTimeSelect={showTime}
                    timeIntervals={15}
                    timeCaption="Time"
                    // Explicit both ways: react-datepicker has no locale
                    // registered, so its own default would always be 12-hour.
                    timeFormat={is24Hour ? 'HH:mm' : 'h:mm aa'}
                    dateFormat={dateFormat}
                    minDate={minDate}
                    maxDate={maxDate}
                    placeholderText={placeholder}
                    disabled={disabled}
                    autoComplete="off"
                />
            </Wrapper>
        </>
    )
}
